import { createContext, useContext, useEffect, useState } from "react";
import { LANGS } from "../i18n/translations";
import { api, trackEvent } from "../lib/api";

const AppContext = createContext(null);

// Default static rates (used until /api/fx-rates resolves with live ECB rates).
export const CURRENCIES = [
  { code: "EUR", symbol: "€", rate: 1.00,  flag: "🇪🇺", name: "Euro" },
  { code: "CHF", symbol: "CHF", rate: 0.96, flag: "🇨🇭", name: "Franc suisse" },
  { code: "CAD", symbol: "$",  rate: 1.50, flag: "🇨🇦", name: "Dollar canadien" },
  { code: "USD", symbol: "$",  rate: 1.08, flag: "🇺🇸", name: "Dollar US" },
];

// Mutable rates object — replaced at runtime when /api/fx-rates returns.
const liveRates = {
  EUR: 1.00,
  CHF: CURRENCIES.find((c) => c.code === "CHF").rate,
  CAD: CURRENCIES.find((c) => c.code === "CAD").rate,
  USD: CURRENCIES.find((c) => c.code === "USD").rate,
};
let liveSource = "fallback";
let liveDate = null;

export function formatPrice(amountEur, currencyCode) {
  const c = CURRENCIES.find((x) => x.code === currencyCode) || CURRENCIES[0];
  const rate = liveRates[c.code] ?? c.rate;
  const v = (Number(amountEur) || 0) * rate;
  if (c.code === "CHF") return `${v.toFixed(2)} ${c.symbol}`;
  if (c.code === "EUR") return `${v.toFixed(2).replace(".", ",")} ${c.symbol}`;
  return `${c.symbol}${v.toFixed(2)}`;
}

// Country → currency mapping (best-effort, all unknowns fall back to EUR)
const COUNTRY_TO_CURRENCY = {
  // Swiss franc
  CH: "CHF", LI: "CHF",
  // Canadian dollar
  CA: "CAD",
  // US dollar (default for the US + countries with USD as legal tender)
  US: "USD", PR: "USD", EC: "USD", SV: "USD", PA: "USD", TL: "USD", ZW: "USD",
  // Euro is the default for everyone else (FR, BE, DE, ES, IT, NL, IE, PT, AT, FI, LU, GR, etc.)
};

function currencyFromCountry(cc) {
  if (!cc) return null;
  return COUNTRY_TO_CURRENCY[cc.toUpperCase()] || "EUR";
}

export function AppProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem("lang") || "fr");
  const [currency, setCurrencyState] = useState(() => localStorage.getItem("currency") || "EUR");
  const [currencyAutoDetected, setCurrencyAutoDetected] = useState(false);
  const [fxInfo, setFxInfo] = useState({ source: "fallback", date: null });
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem("cart") || "[]"); } catch { return []; }
  });
  const [upsellSlug, setUpsellSlug] = useState(null);

  useEffect(() => { localStorage.setItem("cart", JSON.stringify(cart)); }, [cart]);

  useEffect(() => {
    const meta = LANGS.find((l) => l.code === lang) || LANGS[0];
    document.documentElement.dir = meta.dir;
    document.documentElement.lang = meta.code;
    localStorage.setItem("lang", lang);
  }, [lang]);

  useEffect(() => { localStorage.setItem("currency", currency); }, [currency]);

  // Auto-detect currency from country (1× per visitor, can be overridden by user).
  // Order : explicit localStorage choice > geolocation > EUR.
  useEffect(() => {
    if (localStorage.getItem("currency_manually_set")) return;
    let cancelled = false;
    fetch("https://api.country.is", { method: "GET" })
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        const detected = currencyFromCountry(data?.country);
        if (detected && detected !== currency) {
          setCurrencyState(detected);
          setCurrencyAutoDetected(true);
        }
      })
      .catch(() => { /* silent — keep current default */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch live FX rates once on mount (cache 1h server-side).
  useEffect(() => {
    api.get("/fx-rates").then((r) => {
      const { rates, source, date } = r.data || {};
      if (rates && typeof rates === "object") {
        Object.entries(rates).forEach(([k, v]) => { liveRates[k] = Number(v); });
        liveSource = source;
        liveDate = date;
        setFxInfo({ source, date });
      }
    }).catch(() => { /* silent — keep fallback */ });
  }, []);

  function setLang(code) { setLangState(code); }
  function setCurrency(code) {
    if (CURRENCIES.find((c) => c.code === code)) {
      setCurrencyState(code);
      // Mark as manual choice so we don't auto-override on next visit.
      localStorage.setItem("currency_manually_set", "1");
      setCurrencyAutoDetected(false);
    }
  }
  function fmt(amountEur) { return formatPrice(amountEur, currency); }

  function addToCart(product, qty = 1) {
    trackEvent("add_to_cart", {
      product_slug: product.slug,
      category_slug: product.category_slug,
      quantity: qty,
    });
    setCart((prev) => {
      const idx = prev.findIndex((x) => x.product_id === product.id);
      if (idx >= 0) {
        const c = [...prev];
        c[idx] = { ...c[idx], quantity: c[idx].quantity + qty };
        return c;
      }
      const price = product.sale_price || product.price;
      return [...prev, {
        product_id: product.id,
        slug: product.slug,
        resource_slug: product.resource_slug || product.slug,   // vrai slug (pour upsell replace)
        name: product.name?.fr || "",
        image: (product.images || [])[0] || "",
        price,
        is_digital: !!product.is_digital,
        no_shipping: !!(product.no_shipping || product.is_digital),
        quantity: qty,
      }];
    });
    // Trigger upsell (Iter 67) — vérifie si le produit fait partie d'un Pack, ouvre modal si oui.
    // Skip pour les packs eux-mêmes (produit dont slug commence par "pack-" ou est une catégorie pack).
    // On préfère `resource_slug` (vrai slug de la ressource) si présent, sinon `slug`.
    const triggerSlug = product.resource_slug || product.slug || "";
    const isPack = product.is_pack || triggerSlug.startsWith("pack-") || product.category_slug === "packs";
    if (!isPack && triggerSlug && !triggerSlug.startsWith("resource-")) {
      // Pass only the raw slug (not the cart-item slug like "resource-abc123")
      setUpsellSlug(triggerSlug);
    } else if (!isPack && product.resource_slug) {
      setUpsellSlug(product.resource_slug);
    }
  }

  function updateQty(productId, qty) {
    setCart((prev) => prev.map((x) => x.product_id === productId ? { ...x, quantity: Math.max(1, qty) } : x));
  }
  function removeItem(productId) { setCart((prev) => prev.filter((x) => x.product_id !== productId)); }
  function clearCart() { setCart([]); }

  const cartCount = cart.reduce((a, b) => a + b.quantity, 0);
  const cartTotal = cart.reduce((a, b) => a + b.price * b.quantity, 0);

  return (
    <AppContext.Provider value={{
      lang, setLang, currency, setCurrency, fmt, fxInfo, currencyAutoDetected,
      cart, addToCart, updateQty, removeItem, clearCart, cartCount, cartTotal,
      upsellSlug, closeUpsell: () => setUpsellSlug(null),
    }}>{children}</AppContext.Provider>
  );
}

export function useApp() { return useContext(AppContext); }
